'use client';

import React from 'react';
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import type {
  MatchedPlay,
  MatchedPlayWidget,
  PlayItemWidget,
} from 'src/api/redux/profileConfiguration/types';
import {
  WidgetRenderer,
  toProfileWidget,
} from 'src/components/Profiles/shared/widgetRenderer';
import EvidenceInfoIcon from './EvidenceInfoIcon';

/** Select the primary widget for the carousel card preview. */
export const selectCardWidget = (
  widgets: MatchedPlayWidget[] | undefined
): MatchedPlayWidget | undefined =>
  widgets?.find((w) => w.is_primary) ?? widgets?.[0];

/** Filter enrichment widgets, then sort: primary first, rest by display_order. */
export const sortPlayWidgets = (
  widgets: MatchedPlayWidget[] | undefined
): MatchedPlayWidget[] => {
  const filtered = (widgets ?? []).filter((w) => w.code !== 'enrichment_content');
  const primary = filtered.filter((w) => w.is_primary);
  const rest = filtered
    .filter((w) => !w.is_primary)
    .sort((a, b) => (a.display_order ?? Infinity) - (b.display_order ?? Infinity));
  return [...primary, ...rest];
};

interface PlaysCardProps {
  play: MatchedPlay;
  onClick: () => void;
}

const PlaysCard: React.FC<PlaysCardProps> = ({ play, onClick }) => {
  const widget = selectCardWidget(play.widgets);
  if (!widget) return null;

  const profileWidget = toProfileWidget(
    widget as unknown as PlayItemWidget
  );

  // Widgets that manage their own summary placement (e.g. between providers and hero)
  const widgetManagesSummary = profileWidget.code === 'code_set_treating_volume';

  return (
    <div
      role="button"
      tabIndex={0}
      className="snap-center shrink-0 w-80 text-left cursor-pointer"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      data-testid={`plays-card-${play.id}`}
      aria-label={`View ${play.label} details`}
    >
      <div className="rounded-2xl bg-white shadow-sm p-5 flex flex-col gap-2 h-full hover:shadow-lg hover:scale-[1.02] transition-all">
        <div className="flex items-start justify-between gap-2">
          <p
            className="text-base font-bold text-neutral-800"
            data-testid={`plays-card-${play.id}-label`}
          >
            {play.label}
          </p>
          <div className="flex items-center gap-1 shrink-0">
            {profileWidget.evidence && (
              <span
                role="presentation"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <EvidenceInfoIcon
                  evidence={profileWidget.evidence}
                  data-testid={`plays-card-evidence-icon-${play.id}`}
                />
              </span>
            )}
            <span
              className="shrink-0 flex items-center justify-center w-6 h-6"
              data-testid={`plays-card-expand-icon-${play.id}`}
            >
              <Icon
                icon={faUpRightAndDownLeftFromCenter}
                className="w-3 h-3 text-neutral-700"
              />
            </span>
          </div>
        </div>
        {!widgetManagesSummary && (
          <p
            className="text-xs text-neutral-500 line-clamp-2"
            data-testid={`plays-card-${play.id}-summary`}
          >
            {play.summary}
          </p>
        )}
        <WidgetRenderer
          widget={profileWidget}
          variant="preview"
          parentLabel={play.label}
          parentSummary={widgetManagesSummary ? play.summary : undefined}
        />
      </div>
    </div>
  );
};

export default PlaysCard;
